
import React, { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, User, Eye, EyeOff, Phone } from 'lucide-react';
import { PhoneAuth } from './PhoneAuth';
import { type AuthResponse } from '@/api/authService';
import { getAppSettings } from '@/api/settingsService';

type AuthMode = 'social' | 'email' | 'phone';

// Google blocks OAuth inside embedded webviews (disallowed_useragent), so the
// Google button can't work when the site opens in the Facebook/Instagram
// in-app browser — detect it to show a hint instead of a dead button.
const isInAppBrowser = () =>
  typeof navigator !== 'undefined' &&
  /FBAN|FBAV|FB_IAB|Instagram|Messenger/i.test(navigator.userAgent);

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional context line under the title (e.g. why sign-in is required). */
  message?: string;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, message }) => {
  const [authMode, setAuthMode] = useState<AuthMode>('social');
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [phoneAuthEnabled, setPhoneAuthEnabled] = useState(false);
  const { signIn, signUp, signInWithGoogle, signInWithFacebook, signInWithPhone } = useAuth();

  useEffect(() => {
    if (isOpen) {
      getAppSettings().then((settings) => {
        setPhoneAuthEnabled(settings.auth.phoneEnabled);
      });
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await signIn({ email, password });
        if (!error) {
          onClose();
        }
      } else {
        const { error } = await signUp({ email, password, fullName });
        if (!error) {
          onClose();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (payload: { credential?: string; accessToken?: string }) => {
    setLoading(true);
    try {
      const { error } = await signInWithGoogle(payload);
      if (!error) {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  // Custom-button Google flow (access token) — replaces the Google-rendered
  // <GoogleLogin> iframe button so Google and Facebook get visually consistent
  // buttons. The popup posts the token back via window.opener, which requires
  // COOP same-origin-allow-popups (set in backend securityHeaders.ts).
  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      if (tokenResponse.access_token) {
        handleGoogleLogin({ accessToken: tokenResponse.access_token });
      } else {
        toast.error('خطأ في تسجيل الدخول', {
          description: 'لم يتم الحصول على بيانات المستخدم',
        });
      }
    },
    onError: () => {
      toast.error('خطأ في تسجيل الدخول', {
        description: 'حدث خطأ أثناء تسجيل الدخول بواسطة Google',
      });
    },
  });

  const handleFacebookLogin = async () => {
    setLoading(true);
    try {
      await signInWithFacebook();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setFullName('');
    setShowPassword(false);
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    resetForm();
  };

  const handlePhoneAuthSuccess = async (authResponse: AuthResponse) => {
    console.log('Phone auth successful:', authResponse);
    
    // Use the AuthContext signInWithPhone method to properly update state
    await signInWithPhone(authResponse);
    
    // Close the modal after successful authentication
    onClose();
  };

  const getTitle = () => {
    if (authMode === 'phone') return 'تسجيل الدخول برقم الهاتف';
    if (authMode === 'email') return isLogin ? 'تسجيل الدخول' : 'إنشاء حساب جديد';
    return 'تسجيل الدخول';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-bold text-warmOrange-600 font-arabic">
            {getTitle()}
          </DialogTitle>
          {message && (
            <p className="text-center text-sm text-muted-foreground font-arabic pt-1">
              {message}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {authMode === 'phone' && (
            <div>
              <PhoneAuth 
                onSuccess={handlePhoneAuthSuccess}
                onCancel={() => setAuthMode('social')}
              />
            </div>
          )}

          {authMode === 'social' && (
            <>
              {/* Social and Phone Login Options */}
              <div className="space-y-3">
                {phoneAuthEnabled && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full font-arabic"
                    onClick={() => setAuthMode('phone')}
                  >
                    <Phone className="w-5 h-5 ml-2" />
                    تسجيل الدخول برقم الهاتف
                  </Button>
                )}

                {isInAppBrowser() ? (
                  <p className="text-sm text-gray-500 text-center font-arabic border rounded-md p-3">
                    تسجيل الدخول بـ Google غير متاح داخل متصفح فيسبوك/إنستجرام.
                    افتح الموقع في المتصفح (Chrome أو Safari) أو استخدم
                    البريد الإلكتروني
                  </p>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full font-arabic"
                    onClick={() => googleLogin()}
                    disabled={loading}
                  >
                    <svg className="w-5 h-5 ml-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    تسجيل الدخول بـ Google
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="w-full font-arabic"
                  onClick={handleFacebookLogin}
                  disabled={loading}
                >
                  <svg className="w-5 h-5 ml-2" viewBox="0 0 24 24">
                    <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  تسجيل الدخول بـ Facebook
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">أو</span>
                </div>
              </div>

              <Button
                type="button"
                variant="link"
                onClick={() => setAuthMode('email')}
                className="w-full font-arabic text-warmOrange-600"
              >
                <Mail className="w-4 h-4 ml-2" />
                تسجيل الدخول بالبريد الإلكتروني
              </Button>
            </>
          )}

          {authMode === 'email' && (
            <>
              {/* Email/Password Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div>
                    <Label htmlFor="fullName" className="font-arabic">الاسم الكامل</Label>
                    <div className="relative mt-1">
                      <User className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="fullName"
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="pr-10 font-arabic"
                        placeholder="أدخل اسمك الكامل"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="email" className="font-arabic">البريد الإلكتروني</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pr-10 font-arabic"
                      placeholder="أدخل بريدك الإلكتروني"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="font-arabic">كلمة المرور</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10 pl-10 font-arabic"
                      placeholder="أدخل كلمة المرور"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-warmOrange-500 hover:bg-warmOrange-600 font-arabic"
                  disabled={loading}
                >
                  {loading ? 'جاري المعالجة...' : (isLogin ? 'تسجيل الدخول' : 'إنشاء الحساب')}
                </Button>
              </form>

              <div className="text-center space-y-2">
                <Button
                  type="button"
                  variant="link"
                  onClick={toggleMode}
                  className="font-arabic text-warmOrange-600"
                >
                  {isLogin ? 'ليس لديك حساب؟ إنشاء حساب جديد' : 'لديك حساب بالفعل؟ تسجيل الدخول'}
                </Button>
                
                <div>
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setAuthMode('social')}
                    className="font-arabic text-gray-600 text-sm"
                  >
                    العودة إلى خيارات تسجيل الدخول
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
